'use client';

import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';

import AspectRatio, { AspectRatioProps } from '../AspectRatio/AspectRatio';
import Skeleton from '../Skeleton/Skeleton';

import styles from './Image.module.scss';

interface ImageSource {
  '1x': string;
  '2x': string;
}

type ImageType = 'image/webp' | 'image/avif';
type Theme = 'dark' | 'light';

export interface ImageProps {
  uri_desktop: ImageSource;
  uri_tablet?: ImageSource;
  uri_mobile: ImageSource;
  className?: string;
  alt: string;
  aspectRatio?: Omit<AspectRatioProps, 'children'>;
  animateImage?: boolean;
  type?: ImageType;
  draggable?: boolean | 'false' | 'true';
  rounded?: boolean;
  theme?: Theme;
  showSkeleton?: boolean;
  responsive?: boolean;
  useAspectRatio?: boolean;
  /** Fallback image src to display if the primary image fails to load. */
  fallbackSrc?: string;
  /** CSS object-fit value for the img element. Defaults to 'cover'. */
  objectFit?: 'cover' | 'contain';
}

const Image: React.FC<ImageProps> = ({
  uri_desktop,
  uri_tablet,
  uri_mobile,
  className = '',
  alt = '',
  aspectRatio,
  animateImage = false,
  type = 'image/webp',
  draggable = 'false',
  rounded = true,
  theme = 'dark',
  showSkeleton = false,
  responsive = true,
  useAspectRatio = true,
  fallbackSrc,
  objectFit = 'cover',
}) => {
  const [pixelRatio, setPixelRatio] = useState(1);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPixelRatio((window?.devicePixelRatio > 1 ? 2 : 1) || 1);
    if (!containerRef.current || showSkeleton) return;

    const currentContainer = containerRef.current;

    const applyFallback = (img: HTMLImageElement, container: HTMLElement) => {
      container.querySelectorAll('source').forEach((s) => {
        (s as HTMLSourceElement).srcset = '';
      });
      img.src = fallbackSrc!;
    };

    // Extracted to keep observer callback nesting within limits
    const loadEntry = (container: HTMLElement, obs: IntersectionObserver) => {
      const sources = container.querySelectorAll('source');
      sources.forEach((source) => {
        const sourceElement = source as HTMLSourceElement;
        if (sourceElement.dataset.srcset) {
          sourceElement.srcset = sourceElement.dataset.srcset;
        }
      });

      const img = container.querySelector('img') as HTMLImageElement;
      if (img && img.dataset.src) {
        if (fallbackSrc) {
          img.addEventListener('error', () => applyFallback(img, container), { once: true });
        }
        img.src = img.dataset.src;
      }

      setIsImageLoaded(true);
      obs.unobserve(container);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadEntry(entry.target as HTMLElement, observer);
          }
        });
      },
      { threshold: 0.05 } // Start loading when 5% of the image is in the viewport
    );

    observer.observe(currentContainer);

    return () => {
      observer.unobserve(currentContainer);
    };
  }, [uri_desktop, uri_tablet, uri_mobile, showSkeleton, fallbackSrc]);

  if (showSkeleton) {
    return (
      <AspectRatio {...aspectRatio} className={`${className}_aspectRatio`}>
        <Skeleton
          className={styles.imageContainer}
          height="100%"
          borderRadius="4px"
          theme={theme}
        />
      </AspectRatio>
    );
  }

  const imageContent = (
    <div
      ref={containerRef}
      className={classnames(styles.imageContainer, { [styles.rounded]: rounded })}
    >
      <picture>
        <source
          data-srcset={uri_mobile?.[`${pixelRatio}x` as keyof ImageSource]}
          media="(max-width: 767px)"
          type={type}
        />
        <source
          data-srcset={uri_tablet?.[`${pixelRatio}x` as keyof ImageSource]}
          media="(min-width: 768px) and (max-width: 1279px)"
          type={type}
        />
        <source
          data-srcset={uri_desktop?.[`${pixelRatio}x` as keyof ImageSource]}
          media="(min-width: 1280px)"
          type={type}
        />
        <img
          data-src={uri_desktop?.[`${pixelRatio}x` as keyof ImageSource]}
          alt={alt}
          className={classnames(className, styles.image, {
            [styles.animateImage]: animateImage,
            [styles.rounded]: rounded,
            [styles.imageLoaded]: isImageLoaded,
            [styles.imageLoading]: !isImageLoaded,
            [styles.objectFitContain]: objectFit === 'contain',
          })}
          draggable={draggable}
        />
      </picture>
    </div>
  );

  return useAspectRatio ? (
    <AspectRatio {...aspectRatio} className={`${className}_aspectRatio`} responsive={responsive}>
      {imageContent}
    </AspectRatio>
  ) : (
    <div className={className}>{imageContent}</div>
  );
};

Image.displayName = 'Image';

export default Image;
